import type { AuthTokens } from '@mindweb/shared'
import { api } from '@/lib/api'
import { useAuthStore } from '@/lib/store/auth'

export interface GoogleIdClient {
  initialize: (config: {
    client_id: string
    callback: (response: { credential: string }) => void
    auto_select?: boolean
  }) => void
  prompt: () => void
  cancel: () => void
  renderButton: (
    parent: HTMLElement,
    options: {
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'large' | 'medium' | 'small'
      shape?: 'rectangular' | 'pill' | 'circle' | 'square'
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
      logo_alignment?: 'left' | 'center'
      width?: number
    },
  ) => void
}

export function getGoogleId(): GoogleIdClient | null {
  const google = (window as unknown as {
    google?: { accounts?: { id?: GoogleIdClient } }
  }).google
  return google?.accounts?.id ?? null
}

export function getGoogleIdAsync(timeoutMs = 8000): Promise<GoogleIdClient | null> {
  const immediate = getGoogleId()
  if (immediate) return Promise.resolve(immediate)

  return new Promise((resolve) => {
    const start = Date.now()
    const interval = window.setInterval(() => {
      const g = getGoogleId()
      if (g) {
        window.clearInterval(interval)
        resolve(g)
        return
      }
      if (Date.now() - start > timeoutMs) {
        window.clearInterval(interval)
        resolve(null)
      }
    }, 50)
  })
}

export async function handleGoogleCredential(
  idToken: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const result = await api.post<AuthTokens>('/api/auth/google', { idToken })
    if (!result.success || !result.data) {
      return { ok: false, error: result.error ?? 'Authentication failed' }
    }
    useAuthStore.getState().setTokens(result.data.accessToken, result.data.refreshToken)
    return { ok: true }
  } catch (error) {
    console.error('Google credential exchange failed:', error)
    return { ok: false, error: 'Authentication failed. Please try again.' }
  }
}
